import React, { useMemo, useState } from 'react';
import { Transaction } from '../../../types';
import { formatCurrency } from '../../../../../../utils/functions';
import { FaCheckCircle, FaTimesCircle, FaClock, FaChevronDown, FaChevronUp, FaReceipt, FaChartBar } from 'react-icons/fa';

interface TransactionSummaryProps {
  transactions: Transaction[];
}

const TransactionSummary: React.FC<TransactionSummaryProps> = ({ transactions }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getPaymentTypeName = (transaction: Transaction): string => {
    if (Number(transaction.payment_type_id) === 22 && (transaction as any).custom_payment_name) {
      return (transaction as any).custom_payment_name;
    }
    return transaction.payment_type_name || 'Otro';
  };

  const summary = useMemo(() => {
    const total_transactions = transactions.length;
    const total_amount = transactions
      .filter(tx => tx.approved)
      .reduce((acc, tx) => acc + (tx.transaction_amount || 0), 0);
    const total_approved = transactions.filter(tx => tx.approved).length;
    const total_cancelled = transactions.filter(tx => tx.canceled).length;
    const total_pending = total_transactions - total_approved - total_cancelled;
    
    const type_summary: Record<string, { count: number; amount: number }> = {};
    transactions
      .filter(tx => tx.approved)
      .forEach(tx => {
        const type = getPaymentTypeName(tx);
        if (!type_summary[type]) type_summary[type] = { count: 0, amount: 0 };
        type_summary[type].count += 1;
        type_summary[type].amount += tx.transaction_amount || 0;
      });

    const paymentTypeRanking = Object.entries(type_summary)
      .sort(([, a], [, b]) => b.amount - a.amount)
      .slice(0, 4);

    return {
      total_transactions,
      total_amount,
      total_approved,
      total_cancelled,
      total_pending,
      paymentTypeRanking,
      average: total_approved > 0 ? total_amount / total_approved : 0,
    };
  }, [transactions]);

  if (transactions.length === 0) return null;

  const approvalRate = summary.total_transactions > 0 
    ? ((summary.total_approved / summary.total_transactions) * 100).toFixed(0) 
    : '0';

  return (
    <div className="flex flex-col gap-3">
      {/* Mobile Compact Toggle */}
      <div 
        className="flex items-center justify-between lg:hidden mp-stat-card cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-6">
          <div className="flex flex-col">
            <span className="text-lg font-bold tabular-nums" style={{ color: '#FC7556' }}>
              {formatCurrency(summary.total_amount)}
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-40" style={{ color: 'var(--text-color)' }}>
              Total procesado
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold tabular-nums" style={{ color: 'var(--text-color)' }}>
              {summary.total_transactions}
            </span>
            <span className="text-[10px] uppercase tracking-wider opacity-40" style={{ color: 'var(--text-color)' }}>
              Transacciones
            </span>
          </div>
        </div>
        <button className="p-1.5 rounded-lg opacity-40 hover:opacity-70 transition-opacity" style={{ color: 'var(--text-color)' }}>
          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
        </button>
      </div>

      {/* Main Content — always visible on desktop, expandable on mobile */}
      <div className={`flex flex-col gap-3 ${isExpanded ? 'flex' : 'hidden'} lg:flex`}>
        {/* Top Row: Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="mp-stat-card md:col-span-1">
            <div className="mp-stat-card-icon">
              <FaChartBar />
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold tabular-nums" style={{ color: '#FC7556' }}>
                {formatCurrency(summary.total_amount)}
              </span>
              <span className="mp-stat-card-label">Total procesado</span>
            </div>
          </div>

          <div className="mp-stat-card">
            <div className="mp-stat-card-icon">
              <FaReceipt />
            </div>
            <div className="flex flex-col">
              <span className="mp-stat-card-value">{summary.total_transactions}</span>
              <span className="mp-stat-card-label">Transacciones</span>
            </div>
          </div>

          <div className="mp-stat-card">
            <div className="flex flex-col">
              <span className="mp-stat-card-value">{formatCurrency(summary.average)}</span>
              <span className="mp-stat-card-label">Ticket promedio</span>
            </div>
          </div>
        </div>

        {/* Status Row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="mp-status-pill">
            <FaCheckCircle className="mp-status-pill-icon text-emerald-500" />
            <div className="flex flex-col">
              <span className="mp-status-pill-value">{summary.total_approved}</span>
              <span className="mp-status-pill-label">Aprobadas</span>
            </div>
            <span className="mp-status-pill-badge">{approvalRate}%</span>
          </div>

          <div className="mp-status-pill">
            <FaClock className="mp-status-pill-icon text-amber-500" />
            <div className="flex flex-col">
              <span className="mp-status-pill-value">{summary.total_pending}</span>
              <span className="mp-status-pill-label">Pendientes</span>
            </div>
            <span className="mp-status-pill-badge">
              {summary.total_transactions > 0 
                ? ((summary.total_pending / summary.total_transactions) * 100).toFixed(0) 
                : 0}%
            </span>
          </div>

          <div className="mp-status-pill">
            <FaTimesCircle className="mp-status-pill-icon text-red-500" />
            <div className="flex flex-col">
              <span className="mp-status-pill-value">{summary.total_cancelled}</span>
              <span className="mp-status-pill-label">Canceladas</span>
            </div>
            <span className="mp-status-pill-badge">
              {summary.total_transactions > 0 
                ? ((summary.total_cancelled / summary.total_transactions) * 100).toFixed(0) 
                : 0}%
            </span>
          </div>
        </div>

        {/* Payment Types */}
        {summary.paymentTypeRanking.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 px-1">
            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-30 mr-1" style={{ color: 'var(--text-color)' }}>
              Por tipo de pago
            </span>
            {summary.paymentTypeRanking.map(([type, data]) => (
              <div key={type} className="mp-chip">
                <span>{type}</span>
                <span className="mp-chip-value">{formatCurrency(data.amount)}</span>
                <span className="mp-chip-count">{data.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionSummary;
